import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { type SandboxClassAsset } from '../../utils/sandboxCourseApi';
import { downloadSandboxClassAsset, fetchSandboxClassAssets } from '../../redux/sandbox/actions';
import { toggleAuthModal } from '../../redux/authentication/action';
import { useDispatch } from 'react-redux';

type Props = {
    courseId: string;
    /** When false, show locked empty state (not enrolled / signed out). */
    canAccess?: boolean;
    isSignedIn?: boolean;
};

function formatSize(bytes?: number) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Student / enrolled viewer: list class asset ZIPs and download via gated API.
 */
export function SandboxClassAssetsPanel({ courseId, canAccess = true, isSignedIn = true }: Props) {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [assets, setAssets] = useState<SandboxClassAsset[]>([]);
    const [canDownload, setCanDownload] = useState(false);

    const load = useCallback(async () => {
        if (!courseId || !isSignedIn) {
            setAssets([]);
            setCanDownload(false);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res: any = await dispatchPromise(fetchSandboxClassAssets(courseId));
            setAssets(res?.assets || []);
            setCanDownload(Boolean(res?.can_download));
        } catch (e: any) {
            setAssets([]);
            setCanDownload(false);
            if (canAccess) {
                showToastMessage(e?.message || 'Could not load class assets', 'error');
            }
        } finally {
            setLoading(false);
        }
    }, [courseId, isSignedIn, canAccess, dispatchPromise]);

    useEffect(() => {
        void load();
    }, [load]);

    const onDownload = async (asset: SandboxClassAsset) => {
        if (!isSignedIn) {
            dispatch(toggleAuthModal(true));
            return;
        }
        setBusyId(asset._id);
        try {
            const res: any = await dispatchPromise(downloadSandboxClassAsset({ courseId, assetId: asset._id }));
            if (!res?.url) throw new Error('Download link unavailable');
            const a = document.createElement('a');
            a.href = res.url;
            a.download = res.original_filename || `${asset.name}.zip`;
            a.rel = 'noopener';
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (e: any) {
            showToastMessage(e?.message || 'Download failed', 'error');
        } finally {
            setBusyId(null);
        }
    };

    if (!isSignedIn) {
        return (
            <LockedState
                title="Sign in to download"
                body="Class assets are available to enrolled students after you sign in."
                cta="Sign in"
                onCta={() => dispatch(toggleAuthModal(true))}
            />
        );
    }

    if (!canAccess && !canDownload) {
        return (
            <LockedState
                title="Enroll to unlock assets"
                body="ZIP packs and class files unlock after you enroll in this course."
            />
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: OBSIDIAN.primary }} size={28} />
            </Box>
        );
    }

    if (!assets.length) {
        return (
            <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT, fontSize: 14 }}>
                No class assets uploaded yet.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 720 }}>
            {assets.map(asset => (
                <Box
                    key={asset._id}
                    sx={{
                        p: 1.75,
                        borderRadius: 2,
                        bgcolor: OBSIDIAN.surfaceContainer,
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                    }}>
                    <FolderZipOutlinedIcon sx={{ color: OBSIDIAN.primary, mt: 0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 700,
                                fontSize: 14,
                                color: OBSIDIAN.onSurface,
                            }}>
                            {asset.name}
                        </Typography>
                        {asset.description ? (
                            <Typography
                                sx={{
                                    mt: 0.35,
                                    fontSize: 13,
                                    color: OBSIDIAN.onSurfaceVariant,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                    whiteSpace: 'pre-wrap',
                                }}>
                                {asset.description}
                            </Typography>
                        ) : null}
                        <Typography sx={{ mt: 0.5, fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>
                            {formatSize(asset.file_size_bytes)}
                            {asset.original_filename ? ` · ${asset.original_filename}` : ' · ZIP'}
                        </Typography>
                    </Box>
                    <Button
                        startIcon={
                            busyId === asset._id ? (
                                <CircularProgress size={14} sx={{ color: OBSIDIAN.primary }} />
                            ) : (
                                <DownloadOutlinedIcon />
                            )
                        }
                        disabled={busyId === asset._id || !canDownload}
                        onClick={() => void onDownload(asset)}
                        sx={{
                            flexShrink: 0,
                            textTransform: 'none',
                            fontWeight: 700,
                            color: OBSIDIAN.onPrimaryFixed,
                            bgcolor: OBSIDIAN.primary,
                            borderRadius: 999,
                            px: 1.75,
                            '&:hover': { filter: 'brightness(1.05)' },
                        }}>
                        Download
                    </Button>
                </Box>
            ))}
        </Box>
    );
}

function LockedState({ title, body, cta, onCta }: { title: string; body: string; cta?: string; onCta?: () => void }) {
    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: OBSIDIAN.surfaceContainer,
                maxWidth: 480,
                textAlign: 'center',
            }}>
            <LockOutlinedIcon sx={{ color: OBSIDIAN.onSurfaceVariant, mb: 1 }} />
            <Typography sx={{ fontFamily: OBSIDIAN_DISPLAY_FONT, fontWeight: 700, color: OBSIDIAN.onSurface }}>
                {title}
            </Typography>
            <Typography
                sx={{ mt: 0.75, fontSize: 13, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                {body}
            </Typography>
            {cta && onCta ? (
                <Button
                    onClick={onCta}
                    sx={{
                        mt: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        color: OBSIDIAN.onPrimaryFixed,
                        bgcolor: OBSIDIAN.primary,
                        borderRadius: 999,
                        px: 2,
                    }}>
                    {cta}
                </Button>
            ) : null}
        </Box>
    );
}
